// Calendar helpers. A "date" is an ISO calendar date string (YYYY-MM-DD) with no
// time zone; instants are stored UTC and displayed Europe/London.

export const LONDON = 'Europe/London';

export type DateStr = string;

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isIsoDate(s: string): boolean {
  return DATE_RE.test(s) && !Number.isNaN(Date.parse(`${s}T00:00:00Z`));
}

function parts(date: DateStr): [number, number, number] {
  const [y, m, d] = date.split('-').map(Number);
  return [y!, m!, d!];
}

function fromUtcMidnight(ms: number): DateStr {
  return new Date(ms).toISOString().slice(0, 10);
}

function utcMidnight(date: DateStr): number {
  const [y, m, d] = parts(date);
  return Date.UTC(y, m - 1, d);
}

export function addDays(date: DateStr, n: number): DateStr {
  return fromUtcMidnight(utcMidnight(date) + n * 86_400_000);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: DateStr, b: DateStr): number {
  return Math.round((utcMidnight(b) - utcMidnight(a)) / 86_400_000);
}

/** 0 = Sunday … 6 = Saturday, matching JS Date#getDay and `schedules.weekday`. */
export function weekdayOf(date: DateStr): number {
  return new Date(utcMidnight(date)).getUTCDay();
}

/** Inclusive range of dates. */
export function eachDate(from: DateStr, to: DateStr): DateStr[] {
  const out: DateStr[] = [];
  for (let d = from; d <= to; d = addDays(d, 1)) out.push(d);
  return out;
}

export function startOfMonth(date: DateStr): DateStr {
  return `${date.slice(0, 7)}-01`;
}

export function endOfMonth(date: DateStr): DateStr {
  const [y, m] = parts(date);
  return fromUtcMidnight(Date.UTC(y, m, 0));
}

export function addMonths(date: DateStr, n: number): DateStr {
  const [y, m, d] = parts(date);
  const target = new Date(Date.UTC(y, m - 1 + n, 1));
  const last = new Date(Date.UTC(target.getUTCFullYear(), target.getUTCMonth() + 1, 0)).getUTCDate();
  target.setUTCDate(Math.min(d, last));
  return fromUtcMidnight(target.getTime());
}

/** Monday of the week containing `date`. */
export function startOfWeek(date: DateStr): DateStr {
  const wd = weekdayOf(date);
  return addDays(date, wd === 0 ? -6 : 1 - wd);
}

// Bacs non-processing days (England & Wales bank holidays). Bacs follows this
// calendar regardless of where the business is, so charge dates use it.
const BANK_HOLIDAYS = new Set<DateStr>([
  '2025-01-01', '2025-04-18', '2025-04-21', '2025-05-05', '2025-05-26', '2025-08-25', '2025-12-25', '2025-12-26',
  '2026-01-01', '2026-04-03', '2026-04-06', '2026-05-04', '2026-05-25', '2026-08-31', '2026-12-25', '2026-12-28',
  '2027-01-01', '2027-03-26', '2027-03-29', '2027-05-03', '2027-05-31', '2027-08-30', '2027-12-27', '2027-12-28',
  '2028-01-03', '2028-04-14', '2028-04-17', '2028-05-01', '2028-05-29', '2028-08-28', '2028-12-25', '2028-12-26',
]);

export function isWorkingDay(date: DateStr): boolean {
  const wd = weekdayOf(date);
  return wd !== 0 && wd !== 6 && !BANK_HOLIDAYS.has(date);
}

export function addWorkingDays(date: DateStr, n: number): DateStr {
  let d = date;
  let left = n;
  while (left > 0) {
    d = addDays(d, 1);
    if (isWorkingDay(d)) left--;
  }
  return d;
}

export function lastWorkingDayOfMonth(date: DateStr): DateStr {
  let d = endOfMonth(date);
  while (!isWorkingDay(d)) d = addDays(d, -1);
  return d;
}

const londonParts = new Intl.DateTimeFormat('en-GB', {
  timeZone: LONDON,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
});

function londonFields(instant: Date): Record<string, number> {
  const out: Record<string, number> = {};
  for (const p of londonParts.formatToParts(instant)) {
    if (p.type !== 'literal') out[p.type] = Number(p.value);
  }
  return out;
}

/** Calendar date in London for an instant. */
export function londonDate(instant: Date = new Date()): DateStr {
  const f = londonFields(instant);
  return `${f.year}-${String(f.month).padStart(2, '0')}-${String(f.day).padStart(2, '0')}`;
}

/** "HH:MM" wall-clock time in London for an instant. */
export function londonTime(instant: Date): string {
  const f = londonFields(instant);
  return `${String(f.hour).padStart(2, '0')}:${String(f.minute).padStart(2, '0')}`;
}

/** The UTC instant of a London wall-clock time. */
export function londonToUtc(date: DateStr, time = '00:00'): Date {
  const [y, m, d] = parts(date);
  const [hh, mm] = time.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  // London is UTC+0 or UTC+1; correct by the offset observed at the guess.
  const f = londonFields(new Date(guess));
  const seen = Date.UTC(f.year!, f.month! - 1, f.day!, f.hour!, f.minute!);
  return new Date(guess - (seen - guess));
}

const displayDate = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'UTC',
  weekday: 'short',
  day: 'numeric',
  month: 'short',
});

/** "Mon 5 Oct" */
export function formatDate(date: DateStr): string {
  return displayDate.format(new Date(utcMidnight(date)));
}

const WEEKDAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'] as const;
export function weekdayName(weekday: number): string {
  return WEEKDAY_NAMES[weekday] ?? '?';
}
